'use client';

import { useEffect, useState } from 'react';
import { Download, Smartphone, X } from 'lucide-react';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_TTL_DAYS = 30;
const PLAY_URL = 'https://play.google.com/store/apps/details?id=ro.softfactura.app';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface Window {
    /** Evenimentul prins de scriptul din <head>, inainte de hidratare. */
    __sfInstallPrompt?: BeforeInstallPromptEvent;
  }
}

/**
 * Bannerul de instalare.
 *
 * Doua variante, dupa platforma:
 *  - Android -> aplicatia nativa din Google Play (`ro.softfactura.app`).
 *    Bannerul se arata direct, fara sa astepte `beforeinstallprompt`; bara
 *    proprie a Chrome e oprita de scriptul din <head> (layout.tsx), care face
 *    `preventDefault` pe eveniment.
 *  - Desktop (Chrome/Edge) -> PWA, prin `beforeinstallprompt`, ca pana acum.
 *
 * iOS Safari nu suporta `beforeinstallprompt` si nu avem aplicatie iOS, deci
 * acolo nu se arata nimic.
 *
 * Refuzul se tine 30 de zile in localStorage, comun ambelor variante. Cand
 * aplicatia ruleaza deja instalata (display-mode: standalone) bannerul dispare.
 */
export function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [mode, setMode] = useState<'none' | 'play' | 'pwa'>('none');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Aplicatia deja instalata (rulata in standalone)
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Refuzata recent. localStorage poate arunca (navigare privata, cookie-uri
    // blocate) — tratam ca "niciun refuz", nu lasam efectul sa crape.
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed) {
        const dismissedAt = parseInt(dismissed, 10);
        if (Date.now() - dismissedAt < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000) return;
      }
    } catch {
      // ignoram
    }

    if (/android/i.test(navigator.userAgent)) {
      setMode('play');
      return;
    }

    // Evenimentul poate fi venit deja, inainte de hidratare — scriptul din
    // <head> il pastreaza pe `window.__sfInstallPrompt`.
    const captured = window.__sfInstallPrompt;
    if (captured) {
      setDeferredPrompt(captured);
      setMode('pwa');
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setMode('pwa');
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setMode('none');
      setDeferredPrompt(null);
      window.__sfInstallPrompt = undefined;
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignoram — bannerul se inchide oricum pe sesiunea curenta
    }
    setMode('none');
  };

  if (mode === 'none') return null;
  if (mode === 'pwa' && !deferredPrompt) return null;

  const isPlay = mode === 'play';

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="flex items-start gap-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 shadow-lg">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {isPlay ? <Smartphone className="h-5 w-5" /> : <Download className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {isPlay ? 'Aplicația SoftFactura pentru Android' : 'Instalează SoftFactura'}
          </h3>
          <p className="mt-0.5 text-xs text-neutral-600 dark:text-neutral-400">
            {isPlay
              ? 'Emite facturi, încasează și fotografiază bonuri direct de pe telefon.'
              : 'Acces rapid din desktop, fără browser deschis. Funcționează și offline pentru paginile vizitate.'}
          </p>
          <div className="mt-3 flex gap-2">
            {isPlay ? (
              <a
                href={PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDismiss}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90"
              >
                Descarcă din Google Play
              </a>
            ) : (
              <button
                onClick={handleInstall}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90"
              >
                Instalează
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
            >
              Mai târziu
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Închide"
          className="shrink-0 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
